<template>
  <section class="sections-listing-search" :class="{'__builders-listing': company}">
    <!-- Builder Header (Builder Only) -->
    <BlocksBuilderListingHeader v-if="company" :company="company" />

    <!-- Search filters -->
    <ListingSearchFilters />

    <div class="container-fluid">
      <div class="row __property-map-listing-wrapper no-gutters" :style="listingStore.viewType === 'Map' ? `height: ${propertyMapWrapperHeight}px;` : ''">
        <!-- List View -->
        <div v-if="['Map', 'List'].includes(displayViewType)" ref="propertyListingWrapperRef" :class="listingStore.viewType === 'Map' ? 'col-md-6' : 'col-md-12'" class="__property-listing-block-wrapper">
          <!-- View change button -->
          <ElementsButtonsButtonGroup v-model="selectedView" :class="`--${displayViewType}`" :button-items="[{label: 'List', value: 'List', icon: listViewDarkIcon, selectedIcon: listViewIcon}, {label: 'Map', value: 'Map', icon: mapViewDarkIcon, selectedIcon: mapViewIcon}]" />

          <!-- Listing List -->
          <ListingSearchResultsList />

          <!-- Pagination -->
          <ElementsPagination :pagination-data="paginationData" @page-changed="setNewPage" />
        </div>

        <!-- Map View -->
        <div v-if="['Map', 'Mobile-Map'].includes(displayViewType)" :class="displayViewType === 'Map' ? 'col-md-6' : 'col-md-12'" class="__map-listing-block-wrapper">
          <!-- View change button -->
          <ElementsButtonsButtonGroup v-model="selectedView" :class="`--${displayViewType} __mobile-map`" :button-items="[{label: 'List', value: 'List', icon: listViewDarkIcon, selectedIcon: listViewIcon}, {label: 'Map', value: 'Map', icon: mapViewDarkIcon, selectedIcon: mapViewIcon}]" />

          <!-- Listing Map -->
          <client-only>
            <CommonMapLocation :height="propertyMapWrapperHeight" :markers="mapMarkers" filter-by-map-bounds show-position-fixed-map :zoom="6" />
          </client-only>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import {ComputedRef, PropType, Ref} from 'vue';
import listViewIcon from '~/assets/images/icons/list-view.webp';
import mapViewIcon from '~/assets/images/icons/map-view.webp';
import listViewDarkIcon from '~/assets/images/icons/list-view-dark.webp';
import mapViewDarkIcon from '~/assets/images/icons/map-view-dark.webp';
import {ListingType, ListingWidget} from '~/@types/listing';
import {ListingViewType, useListingStore} from '~/store/listing';
import {Address, PaginationData} from '~/@types/common';
import {useRoute, useRouter} from '#app';
import {Company} from '~/@types/company';
import {propertyTypes} from '~/composables/propertyTypes';
import ListingSearchQueryHandler from '~/composables/listingSearchQuery';
import {GoogleMapMarkerProp} from '~/@types/google-map';

// DATA
const listingStore = useListingStore();
const propertyMapWrapperHeight: Ref<number> = ref(1440);
const screenWidth: Ref<number> = ref(1000);
const selectedView: Ref<ListingViewType> = ref('Map');
const mapMarkers: Ref<GoogleMapMarkerProp[]> = ref([]);
const router = useRouter();
const route = useRoute();
const propertyListingWrapperRef: Ref<HTMLDivElement | null> = ref(null);
const {$analyticsTrack} = useNuxtApp();

// PROPS
const props = defineProps({
  company: {
    type: Object as PropType<Company>,
    default: undefined,
  },
});

/**
 * Component MOUNTED!
 */
onMounted(() => {
  setPropertyMapWrapperHeight();
  if (window) {
    window.addEventListener('resize', setPropertyMapWrapperHeight);
    (window as any).openLink = (linkPath: string) => {
      if (route.path !== linkPath) {
        router.push(linkPath);
      }
    };

    if (mapMarkers.value.length < 1) {
      setMapMarkers();
    }
  }
});

/**
 * @_WATCHER screenWidth
 */
watch(() => selectedView.value, (newValue: ListingViewType, oldValue: ListingViewType) => {
  listingStore.setViewType(selectedView.value);

  // Trigger analytics event
  if (newValue !== oldValue) {
    // Track analytics event
    $analyticsTrack({
      action: 'click',
      property: 'listing view type',
      entity: 'Listing',
      displayedLocation: 'Listing Search Results',
      eventData: {
        userIntention: 'passive',
        buttonLabel: newValue,
        buttonAction: `Show ${newValue} view`,
      },
    });
  }
});

/**
 * @_WATCHER listingStore.viewType
 */
watch(() => listingStore.listingData, (newValue, oldValue) => {
  if (listingStore.listingData && JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
    // Track analytics event
    $analyticsTrack({
      action: 'search',
      property: 'listing',
      entity: 'Listing',
      displayedLocation: 'Listing Search Results',
      eventData: {
        userIntention: 'active',
        resultLength: listingStore.listingData.found,
        showed: listingStore.listingData.hits.length,
        searchResultId: listingStore.requestId,
      },
    });

    setMapMarkers();
  }
}, {deep: true});

/**
 * Display view type
 */
const displayViewType: ComputedRef<ListingViewType> = computed(() => {
  if (screenWidth.value > 0 && screenWidth.value < 768 && selectedView.value === 'Map') {
    return 'Mobile-Map';
  } else {
    return selectedView.value;
  }
});

/**
 * Set propertyMapWrapperHeight
 */
function setPropertyMapWrapperHeight () {
  if (window && window.innerHeight) {
    const windowInnerHeight = window.innerHeight;

    if (windowInnerHeight > 500 && windowInnerHeight < 1200) {
      propertyMapWrapperHeight.value = windowInnerHeight - (props.company ? 196 : 120);
    }

    if (screenWidth.value !== window.innerWidth) {
      screenWidth.value = window.innerWidth;
      if (screenWidth.value > 0 && screenWidth.value < 768) {
        selectedView.value = 'List';
      }
    }
  }
}

/**
 * Set map markers
 */
function setMapMarkers (autoTryCount = 0) {
  if (window && window.google && listingStore.listingData && Array.isArray(listingStore.listingData.hits) && listingStore.listingData.hits.length > 0) {
    mapMarkers.value = listingStore.listingData.hits.filter((hit) => hit.document && hit.document.address && hit.document.address.latitude && hit.document.address.longitude && hit.document.category !== 'Ad').map((hit) => ({
      id: hit.document.slug,
      position: new google.maps.LatLng((hit.document.address as Address).latitude, (hit.document.address as Address).longitude),
      title: hit.document.name,
      infoWindowContent: getInfoWindowContent(hit.document),
      color: getPinColor(hit.document),
    }));
  } else if (autoTryCount < 10) {
    setTimeout(() => {
      mapMarkers.value = [];
      setMapMarkers(autoTryCount + 1);
    }, 500);
  }
}

function getPinColor (listing: ListingWidget) {
  if (listing.listingTypes && listing.listingTypes.length) {
    const propertyType = propertyTypes.find((propertyType) => propertyType.value === listing.listingTypes[0]);
    if (propertyType && propertyType.color) {
      return propertyType.color;
    }
  }
  return '#000000';
}

/**
 * Pagination data
 */
const paginationData: ComputedRef<PaginationData> = computed(() => {
  if (listingStore.listingData) {
    return {
      page: listingStore.listingData.page,
      totalRecords: listingStore.listingData.found,
      perPage: listingStore.listingData.request_params.per_page,
    };
  }
  return {
    page: 1,
    totalRecords: 0,
    perPage: 48,
  };
});

/**
 * Set new page
 *
 * @param pageNumber
 */
function setNewPage (pageNumber: number) {
  ListingSearchQueryHandler.set({page: pageNumber});
  if (propertyListingWrapperRef.value) {
    propertyListingWrapperRef.value.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
}

/**
 * Get map info window content
 *
 * @param listing
 */
function getInfoWindowContent (listing: ListingWidget) {
  let iconInfoItems = '';
  if (listing.listingTypes.includes('Land')) {
    iconInfoItems += `<div class="__icon-info-item"><img src="/images/land-size.webp" alt="Land size icon" data-not-lazy><span>${listing.landArea ? listing.landArea : '-'}㎡</span></div>`;
  } else {
    iconInfoItems = `<div class="__icon-info-item"><img src="/images/beds.webp" alt="Bed icon" data-not-lazy><span>${listing.bedrooms ? listing.bedrooms : '-'}</span></div>
                                  <div class="__icon-info-item"><img src="/images/bathrooms.webp" alt="Bathrooms icon" data-not-lazy><span>${listing.bathrooms ? listing.bathrooms : '-'}</span></div>
                                  <div class="__icon-info-item"><img src="/images/land-size.webp" alt="Land size icon" data-not-lazy><span>${listing.landArea ? listing.landArea : '-'}㎡</span></div>`;
  }
  return `<div class="map-listing-info-window">
                              <div class="blocks-listing">
                                <div class="__listing-image-wrapper">
                                  <div class="__listing-image" style="background-image: url('${listing.heroImageUrl}');">
                                  <div class="__listing-type-wrapper">
                                    <div class="__listing-type" style="background: ${listingTypeBgColor(listing.listingTypes[0])}">
                                      ${listing.listingTypes && listing.listingTypes.length ? listing.listingTypes[0] : ''}
                                    </div>
                                  </div>
                                 </div>
                                </div>
                                <div class="__listing-info-wrapper">
                                  <h4 class="__listing-title"> ${listing.name}</h4>
                                  <div class="__listing-address-wrapper">
                                    <img class="__listing-address-icon" src="/images/location.webp" alt="Location icon" data-not-lazy>
                                    <span class="__listing-address-text"> ${listing.address?.formattedAddress}</span>
                                  </div>
                                  <div class="__builder-logo-action-wrapper">
                                    <img src="${listing.company.logoUrl}" alt="${listing.company.name} logo" style="max-width: 60px; max-height: 30px; width: fit-content; height: auto" data-not-lazy>
                                  </div>
                                </div>
                                <div class="__icon-info-bar">
                                  ${iconInfoItems}
                                  <span class="__view-listing-button" onclick="openLink('/listing/${listing.slug}/view');">View&nbsp;&#62;</span>
                                </div>
                              </div>
                            </div>`;
}

/**
 * Get the listing type bg color
 */
function listingTypeBgColor (listingType: ListingType) {
  const listingTypeObj = propertyTypes.find((item) => item.value === listingType);
  return listingTypeObj ? listingTypeObj.color : '#5c52c6';
}
</script>

<style lang="scss">
.sections-listing-search {
  padding: 0;

  .container-fluid {
    padding: 0;
    margin: 0;
  }

  .__property-map-listing-wrapper {
    display: flex;
    position: relative;
    width: 100%;
    padding: 0;
    margin: 0;

    .__property-listing-block-wrapper {
      max-height: fit-content;

      &.col-md-6 {
        max-height: 100%;
        overflow-y: auto;
      }
    }

    .blocks-listing-map {
      height: 100%;
    }

    .__map-listing-block-wrapper.col-md-6 {
      .el-buttons-button-group {
        display: none;
      }
    }

    // Action row map list view switcher
    .el-buttons-button-group {
      position: absolute;
      width: fit-content;
      height: 40px;
      top: 30px;
      right: 30px;
      z-index: 9;

      &.__mobile-map {
        //display: none;
        top: 18px;
        right: 10px;

        .el-button-regular {
          background: #fff;

          &.--selected {
            background: rgb(var(--primary));
          }
        }
      }

      .__full-view {
        .el-button-regular {
          margin-right: 0;
          height: 40px;
          border-radius: 8px;

          &:not(.--selected):not(:hover) {
            background: rgb(var(--background-light));
          }

          &:first-child {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }

          &:last-child {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          }

        }
      }
    }
  }
}

// Map info window
.map-listing-info-window {
  width: 320px;
  height: 140px;

  .blocks-listing {
    display: flex;
    width: 320px;
    height: 140px;
    border-radius: 0;
    margin-bottom: 0 !important;

    .__listing-image-wrapper {
      min-height: 120px;
      min-width: 120px;
      height: 120px;
      width: 120px;
      border-radius: 0;
      position: relative;

      .__listing-image {
        width: 120px;
        height: 120px;
        background-size: cover;
        border-bottom-left-radius: 5px;

        .__listing-type-wrapper {
          bottom: 5px;
          left: 0;
          padding: 0;

          .__listing-type {
            font-size: 10px;
            padding: 3px 8px 7px;
          }
        }
      }
    }

    .__listing-info-wrapper {
      width: 200px;
      min-width: 200px;
      padding: 6px;
      display: flex;
      flex-direction: column;

      .__listing-title {
        color: rgb(var(--text-default));
        font-size: 13px;
        font-weight: 500;
        margin-top: 6px;
      }

      .__listing-address-wrapper {
        display: inline-flex;
        align-items: center;
        height: 40px;
        padding: 0;
        margin-top: 2px;

        .__listing-address-text {
          font-size: 11px;
        }

        img {
          width: 8px;
          height: auto;
        }
      }

      .__builder-logo-action-wrapper {
        width: 100%;
        text-align: left;
        padding-left: 2px;
        margin-top: 3px;
        height: fit-content;
        margin-bottom: 20px;

        img {
          width: auto;
          height: auto;
          max-height: 30px !important;
        }
      }
    }

    .__icon-info-bar {
      position: absolute;
      width: 100%;
      height: 28px;
      background: #fff;
      z-index: 5;
      bottom: 0;
      border-top: 1px solid #e5e5e5;
      display: inline-flex;
      justify-content: space-between;

      .__view-listing-button {
        background: #5C52C6FF;
        color: #fff;
        padding: 4px 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 400;
        box-shadow: 0 0 2px 0 #00000078;
        cursor: pointer;
        font-size: 12px;
        transition: box-shadow 0.2s ease-in-out;
        margin-left: 5px;

        &:hover {
          background: #341ee1;
          transition: all 0.2s ease-in-out;
        }
      }

      .__icon-info-item {
        display: flex;
        align-items: center;
        padding: 4px 8px;

        &:not(:last-child) {
          margin-right: 5px;
        }

        img {
          width: 15px;
          height: auto;
          margin-right: 8px;
        }

        span {
          font-weight: 500;
          color: rgba(var(--text-default), 0.6);
        }
      }
    }

    .__listing-category {
      position: absolute;
      bottom: 26px;
      padding: 2px 5px;
      background: #fff;
      border-top-right-radius: 5px;
      box-shadow: 3px -5px 8px -4px #000000d9;
      color: rgba(var(--text-default), 0.5);
      font-weight: 400;
      z-index: 99;
    }
  }
}
</style>
